import React from "react";
import { motion } from "framer-motion";
import "../assets/css/Comparison.css";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Logo Placeholders (User will provide PNGs)
import comparedlyLogo from "../assets/img/logo/comparedly-logo.png";
import sproutLogo from "../assets/img/comparison/sprout-logo.png";
import insiderLogo from "../assets/img/comparison/insider-logo.png";
import hootsuiteLogo from "../assets/img/comparison/hootsuite-logo.png";
import bufferLogo from "../assets/img/comparison/buffer-logo.png";

const comparisonData = {
    headers: [
        { name: "Feature", logo: null },
        { name: "Comparedly", logo: comparedlyLogo, logoStyle: { height: "30px", width: "auto" } },
        { name: "Sprout Social", logo: sproutLogo, logoStyle: { height: "36px", width: "auto" } },
        { name: "SocialInsider", logo: insiderLogo, logoStyle: { height: "54px", width: "auto" } },
        { name: "Hootsuite", logo: hootsuiteLogo, logoStyle: { height: "58px", width: "auto" } },
        { name: "Buffer", logo: bufferLogo, logoStyle: { height: "46px", width: "auto" } }
    ],
    rows: [
        {
            label: "Pricing Model",
            comparedly: "Zero Per-Seat Tax",
            sprout: "$199+ Per User",
            insider: "$99+ Monthly",
            hootsuite: "$99+ Per User",
            buffer: "$12+ Per Channel",
            isPremium: true
        },
        {
            label: "Demo Friction",
            comparedly: "1-Click (No Login)",
            sprout: "Sales Discovery Call",
            insider: "Signup Required",
            hootsuite: "Signup Required",
            buffer: "Signup Required",
            isPremium: true
        },
        {
            label: "Post-Velocity Tracking",
            comparedly: true,
            sprout: false,
            insider: "Limited",
            hootsuite: false,
            buffer: false
        },
        {
            label: "Deep Benchmarking",
            comparedly: "Pro-Grade",
            sprout: "Enterprise Add-on",
            insider: "Good",
            hootsuite: "Basic",
            buffer: "Vanity Only"
        },
        {
            label: "Reporting Automation",
            comparedly: "Executive Snapshots",
            sprout: "Standard PDF",
            insider: "Manual Focus",
            hootsuite: "Standard PDF",
            buffer: "Simple CSV"
        }
    ]
};

const Comparison = () => {
    return (
        <section className="comparison-section" id="competitors">
            <div className="comparison-container">
                <motion.div
                    className="text-center mb-5"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="section-title">Why Agencies are Making the Switch</h2>
                    <p className="section-subtitle">Better data, faster insights and zero enterprise bloat.</p>
                </motion.div>

                <motion.div
                    className="comparison-table-wrapper"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <table className="comparison-table">
                        <thead>
                            <tr>
                                {comparisonData.headers.map((header, i) => (
                                    <th key={i} className={header.name === "Comparedly" ? "col-header-comparedly" : ""}>
                                        <div className="comp-header-content">
                                            {header.logo && (
                                                <img
                                                    src={header.logo}
                                                    alt={`${header.name} logo`}
                                                    className="comp-logo-img"
                                                    style={header.logoStyle || {}}
                                                    onError={(e) => e.target.style.display = 'none'} // Hide if missing
                                                />
                                            )}
                                            <span className={`comp-name ${header.name === "Comparedly" ? "comp-name-branded" : ""}`}>
                                                {header.name === "Comparedly" ? "Comparedly" : (header.logo ? "" : header.name)}
                                            </span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {comparisonData.rows.map((row, i) => (
                                <tr key={i}>
                                    <td className="feature-row-title">{row.label}</td>

                                    {/* Comparedly Column */}
                                    <td className="col-comparedly">
                                        {typeof row.comparedly === "boolean" ? (
                                            <CheckCircleIcon className="check-icon-premium" />
                                        ) : (
                                            <span className="feature-value-highlight">{row.comparedly}</span>
                                        )}
                                    </td>

                                    {/* Sprout Column */}
                                    <td>
                                        {typeof row.sprout === "boolean" ? (
                                            row.sprout ? <CheckCircleIcon /> : <CancelIcon className="cross-icon-muted" />
                                        ) : (
                                            <span className="comp-value-low">{row.sprout}</span>
                                        )}
                                    </td>

                                    {/* Insider Column */}
                                    <td>
                                        {typeof row.insider === "boolean" ? (
                                            row.insider ? <CheckCircleIcon /> : <CancelIcon className="cross-icon-muted" />
                                        ) : (
                                            <span className="comp-value-low">{row.insider}</span>
                                        )}
                                    </td>

                                    {/* Hootsuite Column */}
                                    <td>
                                        {typeof row.hootsuite === "boolean" ? (
                                            row.hootsuite ? <CheckCircleIcon /> : <CancelIcon className="cross-icon-muted" />
                                        ) : (
                                            <span className="comp-value-low">{row.hootsuite}</span>
                                        )}
                                    </td>

                                    {/* Buffer Column */}
                                    <td>
                                        {typeof row.buffer === "boolean" ? (
                                            row.buffer ? <CheckCircleIcon /> : <CancelIcon className="cross-icon-muted" />
                                        ) : (
                                            <span className="comp-value-low">{row.buffer}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </motion.div>
            </div>
        </section>
    );
};

export default Comparison;
